import ImageOptimize from '@/components/ImageOptimize'
import { labelPositionMapStyle } from '@/constants/positions'
import { useLightbox } from '@/hooks/useLightBox'
import type { ProductDetail } from '@/types'
import { getTranslate } from '@/utils/api-interceptors'
import { useTranslations } from 'next-intl'
import { useState, type FC } from 'react'
import Slider from '../Slider/Slider'

const width = 600 * 4
const height = 600 * 4

interface Props {
  alt: string
  product: ProductDetail
  onClickBottomItem: (index: number) => void
}

const breakpoints = [4320, 2160, 1080, 640, 384, 256, 128]

const ProductGallery: FC<Props> = ({
  alt,
  onClickBottomItem,
  product,
}: Props) => {
  const [index, setIndex] = useState(0)
  const t = useTranslations()
  const { openLightBox, renderLightBox } = useLightbox()
  const { openLightBox: openVideo, renderLightBox: renderVideo } = useLightbox()

  const images = product.images || []

  return (
    <div className='image-summary'>
      {renderLightBox({
        slides: images.map((e, i) => {
          return {
            src: e,
            type: i === 0 && product.videoUrl ? undefined : 'image',
            width,
            height,
            srcSet: breakpoints.map(breakpoint => {
              const breakpointHeight = Math.round((height / width) * breakpoint)
              return {
                src: e,
                width: breakpoint,
                height: breakpointHeight,
              }
            }),
          }
        }),
      })}
      {product.videoUrl
        ? renderVideo({
            slides: [product.videoThumbnail || ''].map((e, i) => {
              return {
                src: product.videoThumbnail || '',
                type: i === 0 && product.videoUrl ? undefined : 'image',
                width,
                videoUrl: product?.videoUrl,
                height,
                srcSet: breakpoints.map(breakpoint => {
                  const breakpointHeight = Math.round(
                    (height / width) * breakpoint
                  )
                  return {
                    src: product.videoThumbnail || '',
                    width: breakpoint,
                    height: breakpointHeight,
                  }
                }),
              }
            }),
            carousel: { finite: true },
            hideZoom: true,
            styles: {
              container: {
                width: 'calc(100vw - 20%)',
                height: 'calc(100vh - 10%)',
                margin: 'auto',
              },
              root: {
                backgroundColor: '#000000c5',
              },
            },
          })
        : ''}
      <Slider beforeChange={(_, nextIndex) => setIndex(nextIndex)}>
        {images.map((e, i) => (
          <div
            className='relative mx-auto h-[427px] w-auto pb-10 outline-none'
            key={i}
          >
            <div className='h-full w-auto p-1 px-[6px]'>
              <ImageOptimize
                className='mx-auto aspect-square h-full w-full cursor-pointer rounded-xl object-contain'
                height={470}
                width={470}
                alt={alt}
                src={e}
                priority={true}
                onClick={() => openLightBox(i)}
              />
            </div>
            {product?.labels.map((item, index) => {
              return (
                <span
                  className={`${labelPositionMapStyle.get(
                    item?.position
                  )} label absolute block scrollbar-hide`}
                  style={{ zIndex: 20 + index }}
                  key={index}
                >
                  <ImageOptimize
                    src={item?.image}
                    alt={getTranslate(item?.name)}
                    width={217}
                    height={40}
                    className='h-auto w-auto'
                  />
                </span>
              )
            })}
          </div>
        ))}
      </Slider>
      <div className='mt-2 flex cursor-pointer justify-center gap-3 text-center text-xs'>
        {images?.[index] && (
          <div
            className='flex h-[80px] w-[80px] flex-col items-center justify-center gap-2 rounded-lg border border-[#D1D5DB] bg-white p-1 py-2'
            onClick={() => openLightBox(0)}
          >
            <ImageOptimize
              src={images?.[index]}
              alt='current product image'
              className='h-[64px] w-full'
            />
          </div>
        )}
        {!!product.videoUrl && (
          <div
            className='flex h-[80px] w-[80px] flex-col items-center gap-2 rounded-lg border border-[#D1D5DB] bg-white p-1 py-2'
            onClick={() => openVideo?.(0)}
          >
            <ImageOptimize
              src='/assets/icons/video.svg'
              alt='specification'
              className='h-[24px] w-full'
            />
            <span>{t('ProductDetail.video')}</span>
          </div>
        )}
        <div
          className='flex h-[80px] w-[80px] flex-col items-center gap-2 rounded-lg border border-[#D1D5DB] bg-white p-1 py-2'
          onClick={() => onClickBottomItem?.(1)}
        >
          <ImageOptimize
            src='/assets/icons/specification.svg'
            alt='specification'
            className='h-[24px] w-full'
          />
          <span>{t('ProductDetail.specification')}</span>
        </div>
        <div
          className='flex h-[80px] w-[80px] flex-col items-center gap-2 rounded-lg border border-[#D1D5DB] bg-white p-1 py-2'
          onClick={() => onClickBottomItem?.(0)}
        >
          <ImageOptimize
            src='/assets/icons/information.svg'
            alt='information'
            className='h-[24px] w-full'
          />
          <span>{t('ProductDetail.information')}</span>
        </div>
        <div
          className='flex h-[80px] w-[80px] flex-col items-center gap-2 rounded-lg border border-[#D1D5DB] bg-white p-1 py-2'
          onClick={() => onClickBottomItem?.(2)}
        >
          <ImageOptimize
            src='/assets/icons/review.svg'
            alt='review'
            className='h-[24px] w-full'
          />
          <span> {t('ProductDetail.review')}</span>
        </div>
      </div>
    </div>
  )
}

export default ProductGallery
